import * as teams from '@microsoft/teams-js';
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';

/**
 * Login com a conta do colégio (Entra ID), sem senha nova:
 *  - dentro do Teams: SSO do próprio Teams (getAuthToken), sem tela de login;
 *  - no navegador: MSAL, com redirecionamento para a página de login da Microsoft.
 * O token vai para a API no cabeçalho X-Conves-Auth.
 */
const env = import.meta.env;
const scope = env.VITE_API_SCOPE as string;
let mode: 'teams' | 'browser' | 'dev' = 'browser';
let msal: PublicClientApplication | null = null;

export interface Launch { inTeams: boolean; deepLink: string | null }

const timeout = (ms: number) => new Promise<never>((_, rej) => setTimeout(() => rej(new Error('timeout')), ms));

export async function initAuth(): Promise<Launch> {
  const query = new URLSearchParams(location.search);
  const fromQuery = query.get('confirmar') ? `confirmar=${query.get('confirmar')}` : query.get('reserva') ? `reserva=${query.get('reserva')}` : query.get('v') ? `v=${query.get('v')}` : null;
  if (env.VITE_DEV_USER) { mode = 'dev'; return { inTeams: false, deepLink: fromQuery }; }
  // Dentro do Teams o SDK responde rápido; fora dele, a inicialização não termina
  try {
    await Promise.race([teams.app.initialize(), timeout(1500)]);
    const ctx = await teams.app.getContext();
    mode = 'teams';
    return { inTeams: true, deepLink: ctx.page.subPageId || fromQuery };
  } catch { /* navegador comum */ }
  mode = 'browser';
  msal = new PublicClientApplication({
    auth: { clientId: env.VITE_CLIENT_ID, authority: `https://login.microsoftonline.com/${env.VITE_TENANT_ID}`, redirectUri: location.origin },
    cache: { cacheLocation: 'localStorage' },
  });
  await msal.initialize();
  const res = await msal.handleRedirectPromise();
  const account = res?.account ?? msal.getAllAccounts()[0];
  if (!account) {
    sessionStorage.setItem('conves-deeplink', fromQuery ?? '');
    await msal.loginRedirect({ scopes: [scope] });
    return new Promise(() => undefined); // a página vai para o login da Microsoft
  }
  msal.setActiveAccount(account);
  const saved = sessionStorage.getItem('conves-deeplink');
  sessionStorage.removeItem('conves-deeplink');
  return { inTeams: false, deepLink: fromQuery ?? (saved || null) };
}

export async function authHeaders(): Promise<Record<string, string>> {
  if (mode === 'dev') return { 'X-Dev-User': env.VITE_DEV_USER as string };
  if (mode === 'teams') return { 'X-Conves-Auth': 'Bearer ' + await teams.authentication.getAuthToken() };
  try {
    const r = await msal!.acquireTokenSilent({ scopes: [scope] });
    return { 'X-Conves-Auth': 'Bearer ' + r.accessToken };
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) await msal!.acquireTokenRedirect({ scopes: [scope] });
    throw err;
  }
}

export async function signOut() {
  if (mode === 'browser' && msal) await msal.logoutRedirect();
}
